import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Grid, Flex, Text, Card, Button, IconButton, AlertDialog, SegmentedControl } from '@radix-ui/themes';
import { Pencil1Icon, TrashIcon, PieChartIcon, Cross2Icon } from '@radix-ui/react-icons';
import UserAvatar from '../common/UserAvatar';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import api from '../../api';
import { updateStudents } from '../../redux/slices/guardianSlice';
import GuardianAddWard from '../../screens/guardian/GuardianAddWard';
import WardDetails from './WardDetails';
import usePaystackUpgrade from '../../hooks/usePaystackUpgrade';
import EmailUpdateForm from '../forms/EmailUpdateForm';
import ConfirmPaymentDialog from '../../components/ConfirmPaymentDialog';

const GuardianStudents = () => {
    const dispatch = useDispatch();
    const guardian = useSelector((state) => state.guardian);
    const students = guardian?.students || [];
    const navigate = useNavigate();
    const [studentToDelete, setStudentToDelete] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [currentStudentView, setCurrentStudentView] = useState(null);
    const [currentView, setCurrentView] = useState('school');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const {
        upgradeStudent,
        showEmailDialog,
        setShowEmailDialog,
        showConfirmPaymentDialog,
        setShowConfirmPaymentDialog,
        baseAmount,
        discountCode,
        setDiscountCode,
        isLoading,
        applyDiscountCode,
        handleConfirmPaymentOpenChange,
        selectedDuration,
        setSelectedDuration,
    } = usePaystackUpgrade();


    const handleDeleteClick = (student) => {
        setStudentToDelete(student);
        setIsDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`students/${studentToDelete.id}`);
            const updatedStudentsList = students.filter((student) => student.id !== studentToDelete.id);
            dispatch(updateStudents(updatedStudentsList));
        } catch (error) {
            console.error('Error deleting student:', error);
        } finally {
            setIsDeleteOpen(false);
            setStudentToDelete(null);
        }
    };

    const handleUpgradeClick = (student, e) => {
        e.preventDefault();
        upgradeStudent({ ...student, payerType: 'guardian' });
    };

    return (
        <Box width="100%" maxWidth="1080px" p="4">
            <Grid columns="12" gap="4">
                <Box gridColumn={{ initial: "span 12", md: "span 8" }}>
                    <Flex direction="column" gap="4">
                        {students.length === 0 ? (
                            <Card direction="row" gap="4" align="center" justify="between">
                                <Text>You have no wards registered yet</Text>
                            </Card>
                        ) : (
                            students.map((student) => (
                                <Card key={student.id}>
                                    <Flex direction="column" gap="2">
                                        <Flex direction="row" gap="2" align="center">
                                            <UserAvatar user={student.user} size="4" />
                                            <Flex direction="column">
                                                <Text size="3" weight="bold">{student.user.firstname} {student.user.lastname}</Text>
                                            </Flex>
                                            {currentStudentView === student.id ? (
                                                <Flex direction="row" gap="2" ml="auto">
                                                    <SegmentedControl.Root size="1" defaultValue="school" value={currentView} onValueChange={(value) => setCurrentView(value)}>
                                                        <SegmentedControl.Item value="school">School</SegmentedControl.Item>
                                                        <SegmentedControl.Item value="class">Class</SegmentedControl.Item>
                                                        <SegmentedControl.Item value="account">Account</SegmentedControl.Item>
                                                    </SegmentedControl.Root>
                                                    <IconButton size="1" variant="soft" onClick={() => setCurrentStudentView(null)}>
                                                        <Cross2Icon />
                                                    </IconButton>
                                                </Flex>
                                            ) : (
                                                <Flex direction="row" gap="2" ml="auto">
                                                    <IconButton className='cursor-pointer' variant="soft" onClick={() => navigate(`/guardian/students/${student.id}`)}>
                                                        <PieChartIcon />
                                                    </IconButton>
                                                    <IconButton variant="soft" onClick={() => setCurrentStudentView(student.id)}>
                                                        <Pencil1Icon />
                                                    </IconButton>
                                                    <IconButton className='cursor-pointer' variant="soft" color="red" onClick={() => handleDeleteClick(student)}>
                                                        <TrashIcon />
                                                    </IconButton>
                                                    <Button
                                                        aria-label={`Upgrade for ${student.user.firstname || 'Unknown'}`}
                                                        className={`cursor-pointer ml-auto ${student?.payment_details?.status === 'active' ? 'bg-indigo-500' : 'bg-green-500'} ${student?.payment_details?.disable_payment && 'bg-gray-400'}`}
                                                        color={student?.payment_details?.status === 'active' ? 'indigo' : 'green'}
                                                        // disabled={student?.payment_details?.status === 'active' || isLoading}
                                                        disabled={isLoading || student?.payment_details?.disable_payment}
                                                        onClick={(e) => handleUpgradeClick(student, e)}
                                                    >
                                                        <p className='text-white'>
                                                            {student?.payment_details?.status === 'active' &&
                                                                student?.payment_details?.details?.end_date &&
                                                                DateTime.fromISO(student.payment_details.details.end_date) > DateTime.now()
                                                                ? `Expires on ${DateTime.fromISO(student.payment_details.details.end_date).toFormat('dd - MM')}`
                                                                : isLoading && selectedStudent?.id === student.id ? 'Processing...' : 'Upgrade'}
                                                        </p>
                                                    </Button>
                                                </Flex>
                                            )}
                                        </Flex>
                                        {currentStudentView === student.id && (
                                            <Box py="4" style={{ borderTop: '1px solid #e0e0e0' }}>
                                                <WardDetails studentDetails={student} view={currentView} />
                                            </Box>
                                        )}
                                    </Flex>
                                </Card>
                            ))
                        )}
                    </Flex>
                    <Box py="4" align="end">
                        <GuardianAddWard />
                    </Box>
                </Box>
            </Grid>

            <AlertDialog.Root open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialog.Content className="bg-white p-4 rounded shadow-lg w-1/3 mx-auto">
                    <AlertDialog.Title>Confirm Delete</AlertDialog.Title>
                    <AlertDialog.Description>
                        Are you sure you want to delete this student? This action cannot be undone.
                    </AlertDialog.Description>
                    <div className="flex justify-end gap-4 mt-4">
                        <Button className='cursor-pointer' variant="soft" onClick={() => setIsDeleteOpen(false)}>
                            Cancel
                        </Button>
                        <Button className='cursor-pointer' variant="solid" color="red" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Root>

            <AlertDialog.Root open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <AlertDialog.Content className="p-4 rounded shadow-lg w-1/3 mx-auto">
                    <AlertDialog.Title>Update Email Address</AlertDialog.Title>
                    <AlertDialog.Description>
                        Please provide an email address to continue with the payment.
                    </AlertDialog.Description>
                    <div className="mt-4">
                        <EmailUpdateForm setShowEmailDialog={setShowEmailDialog} />
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Root>

            <ConfirmPaymentDialog
                showConfirmPaymentDialog={showConfirmPaymentDialog}
                setShowConfirmPaymentDialog={setShowConfirmPaymentDialog}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                isLoading={isLoading}
                applyDiscountCode={applyDiscountCode}
                handleDiscountOpenChange={handleConfirmPaymentOpenChange}
                selectedDuration={selectedDuration}
                setSelectedDuration={setSelectedDuration}
                baseAmount={baseAmount || 4000}
            />
        </Box>
    );
};

export default GuardianStudents;